import { createSlice, createAsyncThunk, isAnyOf } from '@reduxjs/toolkit';
import { POService } from '../../../service/api/po-service';
import { showToast } from 'Core/redux/toast/slice';
import { closeModal, showModal } from 'Core/redux/main/slice';
import { redux } from 'Core/redux';
import { MODAL_TYPES } from 'Feature/components/app-modals';
// import { actions as vendorActions } from '../../vendormasters/store/slice'; // Removed to fix circular dependency

const { PO_ADD_EDIT } = MODAL_TYPES;

export const STORE_KEY = 'po';
export const INITIAL_STATE = {
  data: [],
  loading: false,
  editLoading: false,
  error: null,
  filters: {
    searchText: '',
    vendor: null,
    status: null,
    orderDate: null,
  },
  filterVisible: false,
  tileView: true,
  selectedPO: null,
  editModalVisible: false,
  itemReleases: [],
  itemReleasesLoading: false,
};

export const fetchPOs = createAsyncThunk(
  'po/fetchPOs',
  async (_, { rejectWithValue }) => {
    try {
      const response = await POService.getAllPOs();
      if (response.status === 200) {
        return response.response;
      } else {
        return rejectWithValue(response.errorMessage?.message || 'Failed to load POs');
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to load POs');
    }
  },
);

export const fetchPOsByVendorIds = createAsyncThunk(
  'po/fetchPOsByVendorIds',
  async (vendorIds, { rejectWithValue }) => {
    try {
      const response = await POService.getPOsByVendorIds(vendorIds);
      if (response.status === 200) {
        return response.response;
      } else {
        return rejectWithValue(response.errorMessage?.message || 'Failed to load POs');
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to load POs');
    }
  },
);

export const editPOData = createAsyncThunk(
  'po/editPOData',
  async (id, { rejectWithValue }) => {
    try {
      const response = await POService.getPOById(id);
      if (response.status === 200) {
        return response.response;
      } else {
        return rejectWithValue(response.errorMessage?.message || 'Failed to load PO data');
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to load PO data');
    }
  },
);

export const fetchPOsWithFilters = () => (dispatch, getState) => {
  const filters = getState().po.filters;
  
  if (filters?.vendor?.value) {
    // If vendor is selected, fetch POs for that vendor
    dispatch(fetchPOsByVendorIds([filters.vendor.value]));
  } else {
    // If no vendor selected, fetch all POs
    dispatch(fetchPOs());
  }
};

export const downloadPOsWithFilters = () => (dispatch, getState) => {
  const filters = getState().po.filters;
  
  const filterParams = {
    searchText: filters.searchText,
    status: filters?.status?.value,
    vendor: filters?.vendor?.value,
    orderDate: filters?.orderDate,
  };
  
  // Placeholder implementation for download

  // TODO: Implement actual download functionality
};

export const fetchItemReleases = createAsyncThunk(
  'po/fetchItemReleases',
  async ({ poItemDetailId, shipmentId = 0 }, { rejectWithValue }) => {
    try {
      const response = await POService.getItemReleases(poItemDetailId, shipmentId);
      if (response.status === 200) {
        return response.response;
      } else {
        return rejectWithValue(response.errorMessage?.message || 'Failed to load item releases');
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to load item releases');
    }
  },
);

const poSlice = createSlice({
  name: STORE_KEY,
  initialState: INITIAL_STATE,
  reducers: {
    setSearchText: (state, action) => {
      state.filters.searchText = action.payload;
    },
    setVendor: (state, action) => {
      state.filters.vendor = action.payload;
    },
    setStatus: (state, action) => {
      state.filters.status = action.payload;
    },
    setOrderDate: (state, action) => {
      state.filters.orderDate = action.payload;
    },
    setFilterVisible: (state, action) => {
      state.filterVisible = action.payload;
    },
    setTileView: (state, action) => {
      state.tileView = action.payload;
    },
    resetFilters: (state) => {
      state.filters = INITIAL_STATE.filters;
      state.filterVisible = false;
    },
    setSelectedPO: (state, action) => {
      state.selectedPO = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPOs.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPOs.fulfilled, (state, action) => {
        state.loading = false;
        const poList = Array.isArray(action.payload) && action.payload.length > 0
          ? action.payload || []
          : [];
        state.data = poList.map(po => ({
          ...po,
          id: po.poReceivingID, 
          key: po.poReceivingID, // for Ant Design Table
        }));
      })
      .addCase(fetchPOs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchPOsByVendorIds.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPOsByVendorIds.fulfilled, (state, action) => {
        state.loading = false;
        const poList = Array.isArray(action.payload) && action.payload.length > 0
          ? action.payload || []
          : [];
        state.data = poList.map(po => ({
          ...po,
          id: po.poReceivingID, 
          key: po.poReceivingID, // for Ant Design Table
        }));
      })
      .addCase(fetchPOsByVendorIds.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(editPOData.pending, (state) => {
        state.editLoading = true;
      })
      .addCase(editPOData.fulfilled, (state, action) => {
        state.editLoading = false;
        state.selectedPO = action.payload;
      })
      .addCase(editPOData.rejected, (state, action) => {
        state.editLoading = false;
      })
      .addCase(fetchItemReleases.pending, (state) => {
        state.itemReleasesLoading = true;
        state.itemReleases = [];
      })
      .addCase(fetchItemReleases.fulfilled, (state, action) => {
        state.itemReleasesLoading = false;
        state.itemReleases = action.payload || [];
      })
      .addCase(fetchItemReleases.rejected, (state, action) => {
        state.itemReleasesLoading = false;
        state.itemReleases = [];
      });
  },
});

export const { actions } = poSlice;
const { reducer } = poSlice;
redux.injectReducer(STORE_KEY, reducer);

// Add vendor actions to the slice - Removed to fix circular dependency
// export const { setVendor } = vendorActions;

// Add listener to open modal after PO data is loaded
// redux.listener.startListening({
//   actionCreator: editPOData.fulfilled,
//   effect: (action, listenerApi) => {
//     listenerApi.dispatch(showModal({ id: PO_ADD_EDIT }));
//   },
// });


